import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../components/NtText";
import { useTheme } from "../../context/ThemeContext";

/**
 * Stacked-segment bar showing online / unstable / offline counts.
 *
 *   <NtRegistrationsHealth registrations={[...]} extensionsTotal={280} />
 */
function NtRegistrationsHealth({
  registrations = [],
  extensionsTotal = 0,
  containerStyle,
}) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const { online, unstable, offline } = useMemo(() => {
    const isUnstable = (item) => {
      if (item?.pingstatus && /slow|high|unreach/i.test(item.pingstatus)) return true;
      const pt = parseFloat(item?.pingtime);
      return Number.isFinite(pt) && pt >= 100;
    };
    const unstableCount = registrations.filter(isUnstable).length;
    const onlineCount = registrations.length - unstableCount;
    const offlineCount = Math.max(0, extensionsTotal - registrations.length);
    return {
      online: onlineCount,
      unstable: unstableCount,
      offline: offlineCount,
    };
  }, [registrations, extensionsTotal]);

  const total = Math.max(1, online + unstable + offline);
  const onlinePct = (online / total) * 100;
  const unstablePct = (unstable / total) * 100;
  const offlinePct = (offline / total) * 100;

  return (
    <View style={[styles.container, containerStyle]}>
      <View style={styles.headerRow}>
        <NtText style={styles.headerCount}>{registrations.length}</NtText>
        <NtText style={styles.headerSub}>/ {extensionsTotal} extensions</NtText>
      </View>

      <View style={styles.bar}>
        {online > 0 ? (
          <View style={[styles.segment, { width: `${onlinePct}%`, backgroundColor: theme.green }]} />
        ) : null}
        {unstable > 0 ? (
          <View style={[styles.segment, { width: `${unstablePct}%`, backgroundColor: theme.yellow }]} />
        ) : null}
        {offline > 0 ? (
          <View style={[styles.segment, { width: `${offlinePct}%`, backgroundColor: theme.red }]} />
        ) : null}
      </View>

      <View style={styles.legendRow}>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: theme.green }]} />
          <NtText style={[styles.legendValue, { color: theme.green }]}>{online}</NtText>
          <NtText style={styles.legendLabel}>online</NtText>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: theme.yellow }]} />
          <NtText style={[styles.legendValue, { color: theme.yellow }]}>{unstable}</NtText>
          <NtText style={styles.legendLabel}>unstable</NtText>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: theme.red }]} />
          <NtText style={[styles.legendValue, { color: theme.red }]}>{offline}</NtText>
          <NtText style={styles.legendLabel}>offline</NtText>
        </View>
      </View>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    container: { padding: 14 },
    headerRow: {
      flexDirection: "row",
      alignItems: "baseline",
      gap: 8,
      marginBottom: 6,
    },
    headerCount: {
      fontSize: 24,
      fontWeight: "700",
      color: t.text,
      fontVariant: ["tabular-nums"],
      letterSpacing: -0.4,
    },
    headerSub: { color: t.textDim, fontSize: 12 },
    bar: {
      height: 10,
      borderRadius: 5,
      backgroundColor: t.surface3,
      overflow: "hidden",
      flexDirection: "row",
      marginTop: 4,
    },
    segment: { height: "100%" },
    legendRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginTop: 12,
    },
    legendItem: { flexDirection: "row", alignItems: "center", gap: 4 },
    legendDot: { width: 8, height: 8, borderRadius: 4 },
    legendValue: {
      fontSize: 11,
      fontWeight: "700",
      marginLeft: 4,
    },
    legendLabel: { fontSize: 11, color: t.textDim, marginLeft: 2 },
  });

export default NtRegistrationsHealth;
